<template>
  <PageSection>
    <template v-slot:heading>{{ $t("security.heading") }}</template>
    <template v-slot:description>{{ $t("security.description") }}</template>

    <button class="fpds-Button" data-target="#changePasswordModal">
      {{ $t("security.change-password-button") }}
    </button>
    <div id="changePasswordModal" ref="changePasswordModal" class="fpds-Modal fpds-Modal--dialog">
      <div :class="{ 'u-none': forgotPassword }" class="fpds-Modal-container">
        <div class="fpds-Modal-header">
          <div class="fpds-Modal-heading">{{ $t("change-password.heading") }}</div>
        </div>
        <div class="fpds-Modal-body">
          <div>
            <form class="fpds-Form" autocomplete="off">
              <InputBox
                id="oldPassword"
                v-model="oldPassword"
                :placeholder="$t('change-password.old-password-placeholder')"
                type="password"
                :is-valid="!oldPasswordDirty || oldPasswordValid"
                @on-change="oldPasswordChanged"
              >
                {{ $t("change-password.old-password") }}
                <template v-slot:post-text>
                  <div class="u-marginTopXSmall">
                    <button type="button" class="fpds-Link" @click="showForgotPassword">
                      {{ $t("change-password.forgot-your-password") }}
                    </button>
                  </div>
                </template>
              </InputBox>
              <InputBox
                id="password"
                v-model="password"
                :placeholder="$t('change-password.new-password-placeholder')"
                type="password"
                :caption="newPasswordCaption"
                :is-valid="!passwordBlurred || passwordValid"
                @on-change="passwordChanged"
                @focusout.native="passwordBlurredHandler"
              >
                {{ $t("change-password.new-password") }}
              </InputBox>
              <InputBox
                id="confirmPassword"
                v-model="confirmPassword"
                :placeholder="$t('change-password.confirm-new-password')"
                type="password"
                :is-valid="!confirmPasswordDirty || passwordsMatch"
                @on-change="confirmPasswordChanged"
              >
                {{ $t("change-password.confirm-new-password-placeholder") }}
              </InputBox>

              <div class="fpds-Alert">
                <i class="fpds-Alert-icon fpds-Icon fpds-Icon--medium">info</i>
                <div class="fpds-Alert-content">
                  <div class="fpds-Alert-body">
                    {{ $t("change-password.additional-info") }}
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
        <div class="fpds-Modal-footer">
          <div class="u-floatRight">
            <button class="fpds-Modal-actionCancel fpds-Button">
              {{ $t("change-password.buttons.cancel") }}
            </button>
            <ActionButton
              class="fpds-Modal-actionPrimary"
              button-type="primary"
              :disabled="!formValid"
              :on-click="updatePassword"
              :loading="updating"
            >
              {{ $t("change-password.buttons.change-password") }}
            </ActionButton>
          </div>
        </div>
        <button class="fpds-Modal-close fpds-Button fpds-Button--icon fpds-Button--subtle">
          <i class="fpds-Icon fpds-Icon--small">close</i>
        </button>
      </div>
      <!-- RESET PASSWORD -->
      <div :class="{ 'u-none': !forgotPassword }" class="fpds-Modal-container">
        <div class="fpds-Modal-header">
          <div class="fpds-Modal-heading">{{ $t("reset-password.heading") }}</div>
        </div>
        <div class="fpds-Modal-body">
          <div>
            <p class="u-marginBottomMedium">{{ $t("reset-password.description") }}</p>
            <p class="u-marginBottomNone u-textSecondary">{{ $t("reset-password.note") }}</p>
          </div>
        </div>
        <div class="fpds-Modal-footer">
          <div class="u-floatRight">
            <ActionButton :on-click="cancelForgotPassword">{{
              $t("reset-password.buttons.cancel")
            }}</ActionButton>
            <ActionButton
              class="fpds-Modal-actionPrimary"
              button-type="primary"
              :on-click="resetPassword"
              :loading="sendingPasswordReset"
            >
              {{ $t("reset-password.buttons.reset-password") }}
            </ActionButton>
          </div>
        </div>
        <button
          class="fpds-Modal-close fpds-Button fpds-Button--icon fpds-Button--subtle"
          @click="cancelForgotPassword"
        >
          <i class="fpds-Icon fpds-Icon--small">close</i>
        </button>
      </div>
    </div>
  </PageSection>
</template>

<script>
import _ from "lodash";

import ProfileService from "../services/ProfileService";
import PageSection from "../components/PageSection";
import ActionButton from "../components/ActionButton";
import InputBox from "../components/InputBox";

import "../styles/main.scss";

export default {
  components: { PageSection, InputBox, ActionButton },
  data: () => {
    return {
      oldPassword: "",
      oldPasswordDirty: false,
      password: "",
      passwordDirty: false,
      passwordBlurred: false,
      confirmPassword: "",
      confirmPasswordDirty: false,
      passwordValid: false,
      updating: false,
      passwordPolicy: {},
      validation: {
        noRepeatingCharacters: true,
        hasSpecialCharacters: true,
        hasLettersAndNumbers: true,
        isLongEnough: true,
      },
      profile: {
        lastPasswordReset: undefined,
      },
      forgotPassword: false,
      sendingPasswordReset: false,
    };
  },
  computed: {
    oldPasswordValid() {
      return this.oldPassword && this.oldPassword.length > 0;
    },
    passwordsMatch() {
      return this.password === this.confirmPassword;
    },
    formValid() {
      return this.passwordValid && this.passwordsMatch && this.oldPasswordValid && this.passwordValid !== '' && this.oldPassword !== '';
    },
    newPasswordCaption() {
      if (this.passwordBlurred && !this.passwordValid) {
        return this.$t("change-password.new-password-caption.validation-error", {
          min_length: 10,
        });
      }

      return this.$t("change-password.new-password-caption.good", {
        min_length: 10,
      });
    },
  },
  async mounted() {
    this.modal = window.FPDS.Modal;
    this.modal.initAll();
    this.modalInstance = this.modal.getInstance(this.$refs.changePasswordModal);

    this.observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        const newValue = m.target.getAttribute(m.attributeName);
        this.$nextTick(() => {
          this.clearModal(newValue);
        });
      }
    });

    this.observer.observe(this.$refs.changePasswordModal, {
      attributes: true,
      attributeOldValue: true,
      attributeFilter: ["class"],
    });

    await this.reset();
  },
  beforeDestroy() {
    this.observer.disconnect();
  },
  methods: {
    async reset() {
      const profile = this.$store.state.currentProfile;
      _.merge(this.profile, profile);
    },
    clearModal() {
      this.password = "";
      this.oldPassword = "";
      this.confirmPassword = "";
      this.passwordValid = true;
      this.oldPasswordDirty = false;
      this.passwordDirty = false;
      this.passwordBlurred = false;
      this.confirmPasswordDirty = false;
    },
    closeModal() {
      this.modalInstance.close();
    },
    oldPasswordChanged() {
      this.oldPasswordDirty = true;
    },
    passwordChanged() {
      this.validatePassword();
      this.passwordDirty = true;
    },
    passwordBlurredHandler() {
      this.validatePassword();
      this.passwordBlurred = true;
    },
    confirmPasswordChanged() {
      this.validatePassword();
      this.confirmPasswordDirty = true;
    },
    validatePassword() {
      this.passwordValid = this.password ? true : false;

      this.validation.isLongEnough = this.password.length >= 10;
      this.validation.noRepeatingCharacters = true;
      this.validation.hasSpecialCharacters = /[!@#$%^&*]/.test(this.password);
      this.validation.hasLettersAndNumbers =
        /[a-z]/.test(this.password) && /[A-Z]/.test(this.password) && /[0-9]/.test(this.password);

      this.passwordValid =
        this.passwordValid &&
        this.validation.isLongEnough &&
        this.validation.hasSpecialCharacters &&
        this.validation.hasLettersAndNumbers;
    },
    async updatePassword() {
      this.oldPasswordChanged();
      this.passwordChanged();
      this.confirmPasswordChanged();
      if (this.formValid) {
        this.updating = true;
        try {
          await ProfileService.updatePassword({
            oldPassword: this.oldPassword,
            newPassword: this.password,
          });
          this.$notifications.success(this.$t("change-password.success"));
          setTimeout(() => {
            location.reload();
          }, 3000);
        } catch (e) {
          this.updating = false;
          if (e === "PasswordHistoryError: Password has previously been used") {
            this.$notifications.error(this.$t("change-password.failure-repeated-password"));
          } else {
            this.$notifications.error(this.$t("change-password.failure"));
          }
        }
      }
    },
    showForgotPassword() {
      this.forgotPassword = true;
    },
    cancelForgotPassword() {
      this.forgotPassword = false;
    },
    async resetPassword() {
      this.sendingPasswordReset = true;
      try {
        await ProfileService.resetPassword();
      } finally {
        this.sendingPasswordReset = false;
      }
    },
  },
};
</script>
